import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { filter, takeUntil } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';
import { FormArray, FormGroup } from '@angular/forms';
import {COMMA, ENTER, N} from '@angular/cdk/keycodes';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {MatChipInputEvent} from '@angular/material/chips';
import { CartStepData, CouponResponse, ICart, ICartData, ICartProductDetails, IProductFinalized, IProductMain } from '../../../../../core/models/products.models';
import { IClient } from '../../../../../core/models/clients.models';
import { CART_STEPS, PAGE_NAMES, PRODUCT_FAMILY, PRODUCT_TYPES, TRX_STATUS } from '../../../../../core/helpers/constants';
import { Coupon } from '../../cart-wizard.component';
import { ShopApiService } from '../../../shop.apiservice';
import { AppService } from '../../../../../store/app.service';
import { DataFeedService } from '../../../../../shared/api/datafeed.service';
import { AddPaymentMethodModal } from '../../../../../shared/modals/add-payment-method/add-payment-method.component';
import { PaymentMethodApiService } from '../../../../my-account/payment-method/payment-method.apiservice';
import { SettingsApiService } from '../../../../my-account/payment-settings/settings.apiservice';
import { environment } from '../../../../../../environments/environment';


@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss']
})
export class CheckoutComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  PRODUCT_TYPES = PRODUCT_TYPES;
  CART_STEPS = CART_STEPS;

  @Input() finalizedProducts: Array<IProductFinalized> = [];
  @Input() cartStepData: CartStepData = {} as CartStepData;
  @Input() addingFacebook: boolean = false; 
  @Input() addingGoogle: boolean = false; 
  @Input() addingConsulting: boolean = false; 
  @Input() hasGoogle: boolean = false;
  @Input() hasFacebook: boolean = false;
  @Input() isAdmin: boolean = false;
  @Input() isShopManager: boolean = false;
  @Input() paymentForm!: FormGroup;
  @Input() campForm!: FormGroup;
  @Input() cartForm!: FormGroup;
  @Input() clientsData = new Array<IClient>();
  @Input() selectedClient: IClient = {} as IClient;
  @Input() coupons: Array<Coupon> = [];
  @Input() couponResponse: CouponResponse = {} as CouponResponse;
  @Input() product: IProductMain = {} as IProductMain;

  @Output() previous = new EventEmitter<{ evt: any }>(); 
  @Output() sendOrder = new EventEmitter<{ cart: any, coupon: any }>(); 

  locationTitle: string = '';
  paymentSubmitted = false;
  chosenPayment: number = -1;
  selectedStartTime: number = 0;
  selectedPaymentType: string = '';
  selectedCardType: string | null = null;
  ccFeeEnabled: boolean = false;
  ccFeePercentage: number = 0;
  defaultCCFeePercentage: number = 0;
  customCCFeeConfig: string = '';
  uniqueCouponCodes: Array<string> = [];
  myPaymentMethods: Array<any> = new Array<any>();
  productFinalized: IProductFinalized = {} as IProductFinalized;
  saveCardId = new String();
  currentEnvironment: string = environment.titleLogoText;

  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  constructor(
    private shopService: ShopApiService,
    private modalService: NgbModal,
    private appService: AppService,
    private datafeedService: DataFeedService,
    private paymentMethodApi: PaymentMethodApiService,
    private settingsApi: SettingsApiService,
  ) {
  }

  ngOnInit(): void {
    this.settingsApi.getSettings(['IsCCProcessingFeeEnabled', 'CCProcessingFeePercentage', 'CustomCCProcessingFee'])
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.good) {
          this.ccFeeEnabled = (response.settings.find((x: any) => x.name === 'IsCCProcessingFeeEnabled')?.value ?? 'no').toLowerCase() === 'yes';
          this.ccFeePercentage = parseFloat(response.settings.find((x: any) => x.name === 'CCProcessingFeePercentage')?.value ?? '0');
          this.defaultCCFeePercentage = parseFloat(response.settings.find((x: any) => x.name === 'CCProcessingFeePercentage')?.value ?? '0');
          this.customCCFeeConfig = response.settings.find((x: any) => x.name === 'CustomCCProcessingFee')?.value ?? '';
        }
      });

    this.locationTitle = this.campForm.controls['campaignName'].value;
    if (this.addingConsulting) {
      this.locationTitle = this.locationTitle + ' Campaign Consulting';
      this.campForm.controls['campaignName'].setValue(this.campForm.controls['campaignName'].value + ' Campaign Consulting');
    }
    
    // remove "first" coupon
    if (this.coupons.find(x => x.name.toLowerCase() === 'first') !== undefined) {
      this.removeCoupon(this.coupons.find(x => x.name.toLowerCase() === 'first')!);
    }
    this.fp.agreeTerms.reset();

    this.onPaymentClientChange(this.paymentForm.controls["clientId"].value);
    
    if (this.myPaymentMethods.length === 1) {
      this.choosePayment(0); 
      this.onPaymentMethodChange(this.myPaymentMethods[0].id);
    } else {
      this.chosenPayment = -1;
    }

    this.updateCartProductPrices(null);

    if (!this.showRecurring()) {
      this.selectedStartTime = 1;
    } else {
      this.selectedStartTime = 0;
    }

    this.paymentSubmitted = false;
    
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  get fp() { return this.paymentForm.controls! }

  get signupFeeTotal(): number {
    return this.finalizedProducts.reduce((total, product) => total + (product.data?.signupFee ?? 0), 0);
  }

  get isCreditCard(): boolean {
    if (!this.ccFeeEnabled) return false;
    if (this.selectedPaymentType !== 'Card') return false;
    // Debit cards are exempt; null means cardType not yet populated — treat as credit
    return this.selectedCardType === 'credit';
  }

  get surchargeAmount(): number {
    if (!this.isCreditCard) return 0;
    const base = this.selectedStartTime === 0
      ? (this.cartStepData?.recurringTotal ?? 0) + this.signupFeeTotal
      : this.cartStepData?.total ?? 0;
    return parseFloat((base * (this.ccFeePercentage / 100)).toFixed(2));
  }

  get displayOrderTotal(): number {
    if (this.selectedStartTime === 0) {
      return (this.cartStepData?.recurringTotal ?? 0) + this.signupFeeTotal + this.surchargeAmount;
    }

    return (this.cartStepData?.total ?? 0) + this.surchargeAmount;
  }

  get displayRecurringTotal(): number {
    const recurring = this.cartStepData?.recurringTotal ?? 0;
    if (!this.isCreditCard) return recurring;
    return parseFloat((recurring * (1 + this.ccFeePercentage / 100)).toFixed(2));
  }

  onPrevious(evt: any): void {
    this.previous.emit(evt);
  }

  placeOrder(): void {
    this.doSubmitPaymentForm();
    if ((this.selectedStartTime !== 0) && !this.fp.methodId.errors?.required && !this.fp.agreeTerms.errors?.required && (this.uniqueCouponCodes.length == this.coupons.length)) {
      const cartWithSurcharge = {
        ...this.cartStepData,
        ccProcessingFee: this.surchargeAmount,
        total: (this.cartStepData?.total ?? 0) + this.surchargeAmount
      };
      this.sendOrder.emit({cart: cartWithSurcharge, coupon: this.couponResponse});
    }
  }

  updateCartProductPrices(couponCode: any) {
    var yourDate = new Date();
    const offset = yourDate.getTimezoneOffset();
    yourDate = new Date(yourDate.getTime() - (offset*60*1000));
    let todayAtUserComputer = yourDate.toISOString().split('T')[0];
    let getProductPriceRequest: { productIds: string[], productsAdSpend: any[], couponCode: any, clientId: any, todayAtUserComputer: string }
      = { productIds: [], productsAdSpend: [], couponCode: couponCode, clientId: null, todayAtUserComputer };
    
    for (let p of this.finalizedProducts) {
      getProductPriceRequest.productIds.push(p!.id);
      getProductPriceRequest.productsAdSpend.push(p.adSpend);
    }
    
    this.shopService.getCartProductPrices(getProductPriceRequest)
      .subscribe((res: any) => {
        for (let responseProduct of res.data.cartProductPrices) {
          let p = this.finalizedProducts.find(x => x.id === responseProduct.productId);
          p!.proratePrice = responseProduct.proratedAmount;
          p!.prorateSDate = responseProduct.prorateStartDate;
          p!.prorateEDate = responseProduct.prorateEndDate 
          p!.recurringPrice = responseProduct.recurringPrice;
          p!.recurringStartDate = responseProduct.recurringStartDate;
          p!.isProrated = responseProduct.isProrated;
          if (responseProduct.discount) {
            p!.couponAmount = responseProduct.discount;
            p!.couponApplied = true;
            p!.finalPrice = p!.proratePrice - p!.couponAmount!;
            p!.couponCodes = responseProduct.coupon;
          }
          else {
            p!.couponAmount = undefined;
            p!.couponApplied = false;
            p!.finalPrice = p!.proratePrice;
            p!.couponCodes = "";
          }
        }
        this.couponResponse = {
          isValid: res.data.couponValid,
          code: getProductPriceRequest.couponCode,
          responseMessage: res.data.couponInvalidationReason,
          id: res.data.couponId
        };
        if (this.couponResponse.isValid) {
          this.couponResponse.couponApplied = true;
          this.productFinalized.couponApplied = true;
        }
        this.calculateCartTotalPrices(res.data.discount, res.data);
      })
  }

  calculateCartTotalPrices(discountAmt: number, data: any) {
    let total = 0;
    let recurringTotal = 0;
    let discountTotal = 0;
    let couponCodes = '';
    let cartCouponCodes = '';
    let cartDiscountTotal = 0;
    this.uniqueCouponCodes = [];

    for (let product of this.finalizedProducts) {
      total += product.proratePrice;
      if (product.data.signupFee) {
        total += product.data.signupFee;
      }
      if (product.couponAmount) {
        discountTotal += product.couponAmount
      }
      recurringTotal += product.recurringPrice;
    }

    if (data != null && data.couponsDetails != null) {
      for (let coupon of data.couponsDetails) {
        if (coupon.productId === null) {
          cartDiscountTotal += coupon.discountAmount;
          cartCouponCodes += cartCouponCodes.length > 0 ? ',' + coupon.couponCode : coupon.couponCode;
          this.uniqueCouponCodes.push(coupon.couponCode);
        }
        else {
          if (couponCodes.indexOf(coupon.couponCode) < 0) {
            couponCodes += couponCodes.length > 0 ? ',' + coupon.couponCode : coupon.couponCode;
            this.uniqueCouponCodes.push(coupon.couponCode);
          }
        }
      }
    }

    this.product.totalRecurringPrice = recurringTotal;
    this.product.total = total;
    this.productFinalized.couponAmount = discountTotal;
    this.cartStepData = {
      ...this.cartStepData,
      subtotal: this.product.total,
      productDiscount: discountTotal,
      cartDiscount: cartDiscountTotal,
      productCoupon: couponCodes,
      cartCoupon: cartCouponCodes,
      total: this.product.total - (discountTotal + cartDiscountTotal),
      recurringTotal: this.product.totalRecurringPrice,
      couponDetails: data != null && data.couponsDetails != null ? data.couponsDetails : null
    };
  }

  clickDate(newDate: number) {
    // 2 = 1st of the month, 1 = ASAP, 0 = Unselected
    this.selectedStartTime = newDate;
    if (newDate === 2) {
      // this.updateCartProductPrices('First');
      if (this.coupons.find(x => x.name.toLowerCase() === 'first') !== undefined) {
        this.removeCoupon(this.coupons.find(x => x.name.toLowerCase() === 'first')!);
      }
      this.coupons.push({name: 'first'});
      this.onApplyCoupon();
    }
    else {
      if (this.coupons.find(x => x.name.toLowerCase() === 'first') !== undefined) {
        this.removeCoupon(this.coupons.find(x => x.name.toLowerCase() === 'first')!);
      }
      this.onApplyCoupon();
    }
  }

  showRecurring() {
    return this.finalizedProducts.findIndex(x => x.type === PRODUCT_TYPES.SimpleSubscription.code || x.type === PRODUCT_TYPES.VariableSubscription.code) >= 0;
  }

  getStartDate(): any {
    var startDate = this.finalizedProducts.find(x => x.type === PRODUCT_TYPES.SimpleSubscription.code || x.type === PRODUCT_TYPES.VariableSubscription.code)?.prorateSDate;
    if (startDate === undefined) {
      startDate = new Date(1900);
    }
    return startDate;
  }

  getEndDate(): any {
    var endDate = this.finalizedProducts.find(x => x.type === PRODUCT_TYPES.SimpleSubscription.code || x.type === PRODUCT_TYPES.VariableSubscription.code)?.prorateEDate;
    if (endDate === undefined) {
      endDate = new Date(1900);
    }
    return endDate;
  }

  // Also in cart-wizard.component.ts
  getRecurringDate(): any {
    var recurringDate = this.finalizedProducts.find(x => x.type === PRODUCT_TYPES.SimpleSubscription.code || x.type === PRODUCT_TYPES.VariableSubscription.code)?.recurringStartDate;
    if (recurringDate === undefined) {
      recurringDate = '';
    }
    return recurringDate;
  }

  // Edit Payment Method
  openEdit(id: any) {
    const dialogRef = this.modalService.open(AddPaymentMethodModal, { size: 'lg', backdrop: 'static' });
    dialogRef.componentInstance.iId = id;
    dialogRef.componentInstance.isShopOrder = true;
    dialogRef.dismissed.subscribe(() => {
      // this.location.go('/pages/myaccount/payment-methods/');
      this.onPaymentClientChange(this.paymentForm.controls["clientId"].value);
    });
    dialogRef.closed.subscribe(() => {
      // this.location.go('/pages/myaccount/payment-methods/');
      this.onPaymentClientChange(this.paymentForm.controls["clientId"].value);
    });
    dialogRef.componentInstance.oSaveClicked.subscribe(() => {
      this.appService.fetchAllUserPaymentMethods();
      this.onPaymentClientChange(this.paymentForm.controls["clientId"].value);
    })
    // this.location.go('/pages/myaccount/payment-methods/' + id);
  }

  // Checking and Changing Payment Info
  onPaymentClientChange(clientId: any) {
    this.chosenPayment = -1;
    this.selectedPaymentType = '';
    this.selectedCardType = null;
    var submitId = (clientId.id !== undefined) ? clientId.id : clientId;
    
    const sub = this.datafeedService.getPaymentMethodsByClientId(submitId)
    .subscribe(res => {
      res.data.map((x: any) => x.cardNumber = `****${x.cardNumber}`)
      this.myPaymentMethods = [...res.data];
      this.checkPayment();
      setTimeout(() => { sub.unsubscribe(); }, 0);
    });
    
    this.paymentForm.patchValue({
      methodId: null,
      address: null,
      city: null,
      state: null,
      zip: null,
    });
  }

  // Payment Method Change
  onPaymentMethodChange(pid: any) {
    if (pid && this.chosenPayment !== -1) {
      let e = this.myPaymentMethods.find(x => x.id === pid);
      // console.log(e);
      this.selectedPaymentType = e?.paymentType ?? '';
      this.selectedCardType = e?.cardType ?? null;
      if(this.selectedCardType != null && this.selectedCardType === 'credit'){
        this.setCCFeeValue(e?.countryCode, e?.stateCode);
      }
      this.paymentForm.patchValue({
        methodId: pid,
        address: e.address,
        city: e.city,
        state: e.stateCode,
        zip: e.zip,
      });
    } else {
      this.selectedPaymentType = '';
      this.selectedCardType = null;
      this.paymentForm.patchValue({
        methodId: null,
        address: null,
        city: null,
        state: null,
        zip: null,
      });
    }
  }
  
  onSelectPayment(index: number, paymentId: any) {
    this.choosePayment(index);
    this.onPaymentMethodChange(paymentId);
  }

  // Check for valid date.
  checkPayment() {
    const currentDate = new Date();
    var currentYear = currentDate.getFullYear();
    var currentMonth = currentDate.getMonth() + 1;
    this.myPaymentMethods.forEach((x: any) => {
      this.paymentMethodApi.getPaymentMethod(x.id)
      .pipe(takeUntil(this.destroyed$)).subscribe((response: any) => {
        x.paymentType = response.paymentMethod.paymentType;
        if(response.paymentMethod.paymentType == 'Card'){
          if ((Number(response.paymentMethod.expiry.substring(2, 4) > Number(String(currentYear).substring(2, 4)))) || ((Number(response.paymentMethod.expiry.substring(2, 4) == Number(String(currentYear).substring(2, 4)))) && (Number(response.paymentMethod.expiry.substring(0, 2) >= currentMonth)))) {
            x.expiry = response.paymentMethod.expiry.substring(0, 2) + '/' + response.paymentMethod.expiry.substring(2, 4);
            if (x.id === this.saveCardId) {
              this.choosePayment(this.myPaymentMethods.indexOf(x));
              this.onPaymentMethodChange(x.id);
            }
          }
        } else {
          x.expiry = '';
          if (x.id === this.saveCardId) {
            this.choosePayment(this.myPaymentMethods.indexOf(x));
            this.onPaymentMethodChange(x.id);
          }
        }
      });
    });
  }

  doSubmitPaymentForm() {
    this.paymentSubmitted = true;
    if (this.paymentForm.invalid)
      return false;
    return true;
  }

  // Adding New Method
  onAddNewPaymentMethod() {
    const dialogRef = this.modalService.open(AddPaymentMethodModal, { size: 'lg', backdrop: 'static' });
    dialogRef.componentInstance.isShopOrder = true;
    dialogRef.componentInstance.oSaveClicked.subscribe((response: any) => {
      this.saveCardId = response.paymentMethod.id;
      this.onPaymentClientChange(this.paymentForm.controls["clientId"].value);
    });
  }

  choosePayment(newIndex: number) {
    if (this.chosenPayment === newIndex) {
      this.chosenPayment = -1;
    } else {
      this.chosenPayment = newIndex;
    }
  }

  // Adding Coupon
  addCoupon(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.coupons.push({name: value});
    }
    event.chipInput!.clear();
  }
  
  // Removing Coupon
  removeCoupon(coupon: Coupon): void {
    const index = this.coupons.indexOf(coupon);
    if (index >= 0) {
      this.coupons.splice(index, 1);
    }
  }

  onApplyCoupon() {
    this.cartForm.controls['couponCode'].updateValueAndValidity();
    var couponStr = Array.prototype.map.call(this.coupons, s => s.name).toString(); // "1,2,3"
    this.updateCartProductPrices(couponStr);
    return;
  }

  setCCFeeValue(country: string, state: string) {
    const data = JSON.parse(this.customCCFeeConfig);

    const value =
      data.find((x: any) => x.Country === country && x.State === state)?.Value ??
      data.find((x: any) => x.Country === country && x.State === '*')?.Value ??
      null;

    if(value != null){
      this.ccFeePercentage = value;
    }
    else{
      this.ccFeePercentage = this.defaultCCFeePercentage;
    }
  }

}
